/**
 * Panago Pizza Dashboard
 *
 * Pizza franchise operations dashboard showing franchise-specific KPIs.
 * Uses comprehensive data from panagoData.ts for SLM training compatibility.
 */
import { Card, Text, Group, Stack, Badge, Progress, SimpleGrid, Table, RingProgress, Select, Accordion } from '@mantine/core';
import { useState } from 'react';
import {
  IconTrendingUp,
  IconTrendingDown,
  IconMinus,
  IconMapPin,
  IconCash,
  IconChartBar,
  IconPizza,
  IconTruck,
  IconClock,
  IconStar,
} from '@tabler/icons-react';
import TopNav from '../components/TopNav';
import panagoLogo from '../assets/logos/panago-logo.png';
import { PANAGO_REGIONS, PANAGO_METRICS, getTopStores, getBottomStores, type TimePeriod } from '../data/panagoData';

// Store performance tiers
const STORE_TIERS = [
  { tier: 'Top Performers', count: 34, avgRevenue: 892000, color: '#16a34a' },
  { tier: 'Above Average', count: 58, avgRevenue: 745000, color: '#22c55e' },
  { tier: 'Average', count: 52, avgRevenue: 612000, color: '#f59e0b' },
  { tier: 'Below Average', count: 18, avgRevenue: 485000, color: '#ef4444' },
  { tier: 'Needs Attention', count: 8, avgRevenue: 342000, color: '#dc2626' },
];

// Format currency
const formatCurrency = (value: number, compact = false): string => {
  if (compact) {
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  }
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
};

// Format percentage change with icon
const formatChange = (value: number, invert = false) => {
  const isPositive = invert ? value < 0 : value > 0;
  const isNeutral = value === 0;
  const color = isPositive ? 'green' : isNeutral ? 'gray' : 'red';
  const Icon = isPositive ? IconTrendingUp : isNeutral ? IconMinus : IconTrendingDown;

  return (
    <Group gap={4}>
      <Icon size={14} color={isPositive ? '#16a34a' : isNeutral ? '#6b7280' : '#dc2626'} />
      <Text size="sm" c={color} fw={500}>
        {value > 0 ? '+' : ''}{value.toFixed(1)}%
      </Text>
    </Group>
  );
};

export default function PanagoPage() {
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('MoM');

  const topStores = getTopStores(5);
  const bottomStores = getBottomStores(5);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        background: '#f8f9fa',
      }}
    >
      <TopNav />

      <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
        <Stack gap="lg">
          {/* Header with Logo */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Group gap="md" align="center">
                <img src={panagoLogo} alt="Panago Pizza" style={{ height: 48, width: 'auto' }} />
                <div>
                  <Text size="sm" c="dimmed" style={{ fontFamily: 'Poppins, sans-serif' }}>{PANAGO_METRICS.totalStores} locations across Canada | "Canadian to the Crust"</Text>
                </div>
              </Group>
            </div>
            <Group gap="md">
              <Select
                value={selectedPeriod}
                onChange={(v) => setSelectedPeriod(v as TimePeriod)}
                data={[
                  { value: 'DoD', label: 'Day over Day' },
                  { value: 'WoW', label: 'Week over Week' },
                  { value: 'MoM', label: 'Month over Month' },
                  { value: 'QoQ', label: 'Quarter over Quarter' },
                  { value: 'YoY', label: 'Year over Year' },
                ]}
                size="sm"
                w={180}
              />
            </Group>
          </Group>

          {/* Key Financial Metrics */}
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Total Revenue</Text>
                <IconCash size={20} color="#6b7280" />
              </Group>
              <Text size="xl" fw={700}>{formatCurrency(PANAGO_METRICS.totalRevenue.current, true)}</Text>
              <Group gap="xs" mt="xs">
                <Text size="xs" c="dimmed">{selectedPeriod}:</Text>
                {formatChange(PANAGO_METRICS.totalRevenue[selectedPeriod])}
              </Group>
            </Card>

            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Same-Store Sales</Text>
                <IconChartBar size={20} color="#6b7280" />
              </Group>
              <Text size="xl" fw={700}>+{PANAGO_METRICS.sameStoreSales.current}%</Text>
              <Group gap="xs" mt="xs">
                <Text size="xs" c="dimmed">{selectedPeriod}:</Text>
                {formatChange(PANAGO_METRICS.sameStoreSales[selectedPeriod])}
              </Group>
            </Card>

            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Avg Ticket</Text>
                <IconCash size={20} color="#6b7280" />
              </Group>
              <Text size="xl" fw={700}>${PANAGO_METRICS.avgTicket.current.toFixed(2)}</Text>
              <Group gap="xs" mt="xs">
                <Text size="xs" c="dimmed">{selectedPeriod}:</Text>
                {formatChange(PANAGO_METRICS.avgTicket[selectedPeriod])}
              </Group>
            </Card>

            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Annual Orders</Text>
                <IconPizza size={20} color="#6b7280" />
              </Group>
              <Text size="xl" fw={700}>{(PANAGO_METRICS.orderCount.current / 1000000).toFixed(1)}M</Text>
              <Group gap="xs" mt="xs">
                <Text size="xs" c="dimmed">{selectedPeriod}:</Text>
                {formatChange(PANAGO_METRICS.orderCount[selectedPeriod])}
              </Group>
            </Card>
          </SimpleGrid>

          {/* Cost & Operations Row */}
          <SimpleGrid cols={{ base: 1, md: 2 }}>
            {/* Prime Cost Breakdown */}
            <Card withBorder padding="lg" radius="md">
              <Text size="md" fw={600} mb="md" ta="center">Prime Cost Analysis</Text>
              <Group align="flex-start" gap="xl">
                <RingProgress
                  size={140}
                  thickness={14}
                  sections={[
                    { value: PANAGO_METRICS.foodCost.current, color: '#dc2626' },
                    { value: PANAGO_METRICS.laborCost.current, color: '#f59e0b' },
                    { value: 100 - PANAGO_METRICS.primeRatio.current, color: '#e5e7eb' },
                  ]}
                  label={
                    <div style={{ textAlign: 'center' }}>
                      <Text size="xl" fw={700}>{PANAGO_METRICS.primeRatio.current}%</Text>
                      <Text size="xs" c="dimmed">Prime</Text>
                    </div>
                  }
                />
                <Stack gap="sm" style={{ flex: 1 }}>
                  <div>
                    <Group justify="space-between" mb={4}>
                      <Group gap="xs">
                        <div style={{ width: 12, height: 12, background: '#dc2626', borderRadius: 2 }} />
                        <Text size="sm">Food Cost</Text>
                      </Group>
                      <Group gap="xs">
                        <Text size="sm" fw={500}>{PANAGO_METRICS.foodCost.current}%</Text>
                        {formatChange(PANAGO_METRICS.foodCost[selectedPeriod], true)}
                      </Group>
                    </Group>
                    <Progress value={PANAGO_METRICS.foodCost.current} color="red" size="sm" />
                    <Text size="xs" c="dimmed" mt={2}>Target: 28-32%</Text>
                  </div>
                  <div>
                    <Group justify="space-between" mb={4}>
                      <Group gap="xs">
                        <div style={{ width: 12, height: 12, background: '#f59e0b', borderRadius: 2 }} />
                        <Text size="sm">Labor Cost</Text>
                      </Group>
                      <Group gap="xs">
                        <Text size="sm" fw={500}>{PANAGO_METRICS.laborCost.current}%</Text>
                        {formatChange(PANAGO_METRICS.laborCost[selectedPeriod], true)}
                      </Group>
                    </Group>
                    <Progress value={PANAGO_METRICS.laborCost.current} color="yellow" size="sm" />
                    <Text size="xs" c="dimmed" mt={2}>Target: 25-30%</Text>
                  </div>
                  <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 8, marginTop: 4 }}>
                    <Group justify="space-between">
                      <Text size="sm" fw={600}>Prime Ratio</Text>
                      <Group gap="xs">
                        <Text size="sm" fw={600}>{PANAGO_METRICS.primeRatio.current}%</Text>
                        {formatChange(PANAGO_METRICS.primeRatio[selectedPeriod], true)}
                      </Group>
                    </Group>
                    <Text size="xs" c="dimmed" mt={2}>Target: &lt;60% | Industry avg: 55-65%</Text>
                  </div>
                </Stack>
              </Group>
            </Card>

            {/* Operational KPIs */}
            <Card withBorder padding="lg" radius="md">
              <Text size="md" fw={600} mb="md" ta="center">Operational Performance</Text>
              <Stack gap="md">
                <Group justify="space-between">
                  <Group gap="sm">
                    <IconTruck size={20} color="#6b7280" />
                    <div>
                      <Text size="sm" fw={500}>Avg Delivery Time</Text>
                      <Text size="xs" c="dimmed">From order to door</Text>
                    </div>
                  </Group>
                  <Group gap="xs">
                    <Text size="lg" fw={600}>{PANAGO_METRICS.avgDeliveryTime} min</Text>
                  </Group>
                </Group>

                <Group justify="space-between">
                  <Group gap="sm">
                    <IconStar size={20} color="#6b7280" />
                    <div>
                      <Text size="sm" fw={500}>Customer Satisfaction</Text>
                      <Text size="xs" c="dimmed">Average rating</Text>
                    </div>
                  </Group>
                  <Group gap="xs">
                    <Text size="lg" fw={600}>{PANAGO_METRICS.customerSatisfaction}/5.0</Text>
                  </Group>
                </Group>

                <Group justify="space-between">
                  <Group gap="sm">
                    <IconClock size={20} color="#6b7280" />
                    <div>
                      <Text size="sm" fw={500}>On-Time Delivery</Text>
                      <Text size="xs" c="dimmed">Within promised window</Text>
                    </div>
                  </Group>
                  <Group gap="xs">
                    <Text size="lg" fw={600}>87.2%</Text>
                    <Badge color="green" variant="light" size="sm">+1.8%</Badge>
                  </Group>
                </Group>

                <Group justify="space-between">
                  <Group gap="sm">
                    <IconPizza size={20} color="#6b7280" />
                    <div>
                      <Text size="sm" fw={500}>Order Accuracy</Text>
                      <Text size="xs" c="dimmed">Correct orders delivered</Text>
                    </div>
                  </Group>
                  <Group gap="xs">
                    <Text size="lg" fw={600}>96.4%</Text>
                    <Badge color="green" variant="light" size="sm">+0.3%</Badge>
                  </Group>
                </Group>
              </Stack>
            </Card>
          </SimpleGrid>

          {/* Regional Performance with Expandable Stores */}
          <Card withBorder padding="lg" radius="md">
            <Text size="md" fw={600} mb="md" ta="center">Regional Performance</Text>
            <Accordion chevronPosition="left" variant="separated">
              {PANAGO_REGIONS.map((region) => {
                const primeRatio = region.foodCost.current + region.laborCost.current;
                const primeColor = primeRatio < 55 ? 'green' : primeRatio < 60 ? 'yellow' : 'red';
                return (
                  <Accordion.Item key={region.region} value={region.region}>
                    <Accordion.Control>
                      <Stack gap={0} style={{ flex: 1 }}>
                        <Group gap="xs">
                          <IconMapPin size={16} color="#6b7280" />
                          <Text fw={600} size="sm">{region.region}</Text>
                          <Badge size="sm" variant="light">{region.storeCount} stores</Badge>
                        </Group>
                        <Group justify="flex-end" gap="xl" style={{ width: '100%', marginTop: '8px' }}>
                          <Stack gap={0} align="flex-end" style={{ minWidth: '120px' }}>
                            <Text size="xs" c="dimmed">Revenue</Text>
                            <Text size="sm" fw={600}>{formatCurrency(region.revenue.current, true)}</Text>
                          </Stack>
                          <Stack gap={0} align="flex-end" style={{ minWidth: '140px' }}>
                            <Text size="xs" c="dimmed">Same Store Sales</Text>
                            <Badge color={region.sameStoreSales[selectedPeriod] > 3.5 ? 'green' : region.sameStoreSales[selectedPeriod] > 2.5 ? 'yellow' : 'red'} variant="light">
                              +{region.sameStoreSales[selectedPeriod]}% {selectedPeriod}
                            </Badge>
                          </Stack>
                          <Stack gap={0} align="flex-end" style={{ minWidth: '110px' }}>
                            <Text size="xs" c="dimmed">Prime Cost</Text>
                            <Badge color={primeColor} variant="light">{primeRatio.toFixed(1)}%</Badge>
                          </Stack>
                        </Group>
                      </Stack>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap="sm">
                        <Text size="sm" fw={600} c="dimmed" tt="uppercase">Store Performance Details</Text>
                        <Table striped highlightOnHover>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Store</Table.Th>
                              <Table.Th>City</Table.Th>
                              <Table.Th style={{ textAlign: 'right' }}>Revenue</Table.Th>
                              <Table.Th style={{ textAlign: 'right' }}>{selectedPeriod}</Table.Th>
                              <Table.Th style={{ textAlign: 'right' }}>Avg Ticket</Table.Th>
                              <Table.Th style={{ textAlign: 'right' }}>Food Cost</Table.Th>
                              <Table.Th style={{ textAlign: 'right' }}>Rating</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                        <Table.Tbody>
                          {region.stores.slice(0, 10).map((store) => (
                            <Table.Tr key={store.id}>
                              <Table.Td>
                                <Group gap="xs">
                                  {store.isTopPerformer && <Badge size="xs" color="green">Top</Badge>}
                                  {store.isBottomPerformer && <Badge size="xs" color="red">Low</Badge>}
                                  <Text size="sm">{store.name}</Text>
                                </Group>
                              </Table.Td>
                              <Table.Td><Text size="sm">{store.city}</Text></Table.Td>
                              <Table.Td style={{ textAlign: 'right' }}>{formatCurrency(store.revenue.current, true)}</Table.Td>
                              <Table.Td style={{ textAlign: 'right' }}>{formatChange(store.revenue[selectedPeriod])}</Table.Td>
                              <Table.Td style={{ textAlign: 'right' }}>${store.avgTicket.current.toFixed(2)}</Table.Td>
                              <Table.Td style={{ textAlign: 'right' }}>{store.foodCost.current}%</Table.Td>
                              <Table.Td style={{ textAlign: 'right' }}>
                                <Badge color={store.customerSatisfaction >= 4.5 ? 'green' : store.customerSatisfaction >= 4.0 ? 'yellow' : 'red'}>
                                  {store.customerSatisfaction}
                                </Badge>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                        </Table>
                        {region.stores.length > 10 && (
                          <Text size="xs" c="dimmed" mt="xs" ta="center">
                            Showing 10 of {region.stores.length} stores
                          </Text>
                        )}
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          </Card>

          {/* Top and Bottom Performers */}
          <SimpleGrid cols={{ base: 1, md: 2 }}>
            {/* Top Performers */}
            <Card withBorder padding="lg" radius="md">
              <Text size="md" fw={600} mb="md" ta="center">Top Performers</Text>
              <Group justify="flex-end" mb="md">
                <Badge color="green">By Revenue</Badge>
              </Group>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>#</Table.Th>
                    <Table.Th>Store</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Revenue</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>{selectedPeriod}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {topStores.map((store, idx) => (
                    <Table.Tr key={store.id}>
                      <Table.Td>
                        <Badge color={idx === 0 ? 'yellow' : 'gray'} size="sm">#{idx + 1}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <div>
                          <Text size="sm" fw={500}>{store.name}</Text>
                          <Text size="xs" c="dimmed">{store.city}, {store.province}</Text>
                        </div>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatCurrency(store.revenue.current, true)}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatChange(store.revenue[selectedPeriod])}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Card>

            {/* Bottom Performers */}
            <Card withBorder padding="lg" radius="md">
              <Text size="md" fw={600} mb="md" ta="center">Needs Attention</Text>
              <Group justify="flex-end" mb="md">
                <Badge color="red">By Revenue</Badge>
              </Group>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>#</Table.Th>
                    <Table.Th>Store</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Revenue</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>{selectedPeriod}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {bottomStores.map((store, idx) => (
                    <Table.Tr key={store.id}>
                      <Table.Td>
                        <Badge color="red" size="sm">#{idx + 1}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <div>
                          <Text size="sm" fw={500}>{store.name}</Text>
                          <Text size="xs" c="dimmed">{store.city}, {store.province}</Text>
                        </div>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatCurrency(store.revenue.current, true)}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatChange(store.revenue[selectedPeriod])}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Card>
          </SimpleGrid>

          {/* Store Performance Distribution */}
          <Card withBorder padding="lg" radius="md">
            <Text size="md" fw={600} mb="md" ta="center">Store Performance Distribution</Text>
            <SimpleGrid cols={{ base: 2, md: 5 }}>
              {STORE_TIERS.map((tier) => (
                <Card key={tier.tier} withBorder padding="md" radius="md" style={{ borderLeftWidth: 4, borderLeftColor: tier.color }}>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{tier.tier}</Text>
                  <Group justify="space-between" align="flex-end" mt="xs">
                    <Text size="xl" fw={700}>{tier.count}</Text>
                    <Text size="xs" c="dimmed">stores</Text>
                  </Group>
                  <Text size="sm" c="dimmed" mt={4}>
                    Avg: {formatCurrency(tier.avgRevenue, true)}/yr
                  </Text>
                </Card>
              ))}
            </SimpleGrid>
          </Card>

          {/* Time Period Comparison Table */}
          <Card withBorder padding="lg" radius="md">
            <Text size="md" fw={600} mb="md" ta="center">Performance Trends</Text>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Metric</Table.Th>
                  <Table.Th>Current</Table.Th>
                  <Table.Th>DoD</Table.Th>
                  <Table.Th>WoW</Table.Th>
                  <Table.Th>MoM</Table.Th>
                  <Table.Th>QoQ</Table.Th>
                  <Table.Th>YoY</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Td><Text fw={500}>Revenue</Text></Table.Td>
                  <Table.Td>{formatCurrency(PANAGO_METRICS.totalRevenue.current, true)}</Table.Td>
                  <Table.Td>{formatChange(PANAGO_METRICS.totalRevenue.DoD)}</Table.Td>
                  <Table.Td>{formatChange(PANAGO_METRICS.totalRevenue.WoW)}</Table.Td>
                  <Table.Td>{formatChange(PANAGO_METRICS.totalRevenue.MoM)}</Table.Td>
                  <Table.Td>{formatChange(PANAGO_METRICS.totalRevenue.QoQ)}</Table.Td>
                  <Table.Td>{formatChange(PANAGO_METRICS.totalRevenue.YoY)}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td><Text fw={500}>Same-Store Sales</Text></Table.Td>
                  <Table.Td>+{PANAGO_METRICS.sameStoreSales.current}%</Table.Td>
                  <Table.Td>{formatChange(PANAGO_METRICS.sameStoreSales.DoD)}</Table.Td>
                  <Table.Td>{formatChange(PANAGO_METRICS.sameStoreSales.WoW)}</Table.Td>
                  <Table.Td>{formatChange(PANAGO_METRICS.sameStoreSales.MoM)}</Table.Td>
                  <Table.Td>{formatChange(PANAGO_METRICS.sameStoreSales.QoQ)}</Table.Td>
                  <Table.Td>{formatChange(PANAGO_METRICS.sameStoreSales.YoY)}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td><Text fw={500}>Avg Ticket</Text></Table.Td>
                  <Table.Td>${PANAGO_METRICS.avgTicket.current.toFixed(2)}</Table.Td>
                  <Table.Td>{formatChange(PANAGO_METRICS.avgTicket.DoD)}</Table.Td>
                  <Table.Td>{formatChange(PANAGO_METRICS.avgTicket.WoW)}</Table.Td>
                  <Table.Td>{formatChange(PANAGO_METRICS.avgTicket.MoM)}</Table.Td>
                  <Table.Td>{formatChange(PANAGO_METRICS.avgTicket.QoQ)}</Table.Td>
                  <Table.Td>{formatChange(PANAGO_METRICS.avgTicket.YoY)}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td><Text fw={500}>Food Cost</Text></Table.Td>
                  <Table.Td>{PANAGO_METRICS.foodCost.current}%</Table.Td>
                  <Table.Td>{formatChange(PANAGO_METRICS.foodCost.DoD, true)}</Table.Td>
                  <Table.Td>{formatChange(PANAGO_METRICS.foodCost.WoW, true)}</Table.Td>
                  <Table.Td>{formatChange(PANAGO_METRICS.foodCost.MoM, true)}</Table.Td>
                  <Table.Td>{formatChange(PANAGO_METRICS.foodCost.QoQ, true)}</Table.Td>
                  <Table.Td>{formatChange(PANAGO_METRICS.foodCost.YoY, true)}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td><Text fw={500}>Labor Cost</Text></Table.Td>
                  <Table.Td>{PANAGO_METRICS.laborCost.current}%</Table.Td>
                  <Table.Td>{formatChange(PANAGO_METRICS.laborCost.DoD, true)}</Table.Td>
                  <Table.Td>{formatChange(PANAGO_METRICS.laborCost.WoW, true)}</Table.Td>
                  <Table.Td>{formatChange(PANAGO_METRICS.laborCost.MoM, true)}</Table.Td>
                  <Table.Td>{formatChange(PANAGO_METRICS.laborCost.QoQ, true)}</Table.Td>
                  <Table.Td>{formatChange(PANAGO_METRICS.laborCost.YoY, true)}</Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Card>
        </Stack>
      </div>
    </div>
  );
}
